/**
 * @description PTO (Paid Time Off) Settings panel for default allocations and policies
 *
 * Key features:
 * - Default annual allocation hours (vacation, sick, personal)
 * - Accrual method selection (lump_sum, accrual, responsible)
 * - Delayed start configuration for new hires
 * - Allow negative balance toggle
 * - Require approval toggle
 * - Hours per day configuration
 * - All sections are collapsible for better UX
 *
 * ENTERPRISE NOTES:
 * - PTO settings are DEFAULT ENABLED for all merchants
 * - Allocations define defaults for new employee balances
 * - "Responsible Time Off" provides unlimited PTO with approval required
 * - Delayed Start allows companies to require a waiting period for new hires
 * - Draft state managed by parent GlobalSettingsPage
 *
 * @dependencies
 * - Parent provides getValue/updateDraft for draft state management
 *
 * @see .claude/tasks/time-clock-employee-portal.md - Phase 6
 */

import React from 'react';
import { Calendar, ChevronDown, DollarSign, UserCheck, Timer, ShieldCheck, Check, Clock, AlertCircle, Infinity, Hourglass } from 'lucide-react';
import { Disclosure, Switch, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const PTOSettingsPanel = ({
  settings,
  getValue,
  updateDraft,
  saving
}) => {
  // Find specific settings
  const accrualMethod = settings.find(s => s.key === 'pto.accrual_method');
  const accrualFrequency = settings.find(s => s.key === 'pto.accrual_frequency');
  const lumpSumGrantTiming = settings.find(s => s.key === 'pto.lump_sum_grant_timing');
  const delayedStartEnabled = settings.find(s => s.key === 'pto.delayed_start_enabled');
  const delayedStartDays = settings.find(s => s.key === 'pto.delayed_start_days');
  const vacationHours = settings.find(s => s.key === 'pto.vacation_hours_per_year');
  const sickHours = settings.find(s => s.key === 'pto.sick_hours_per_year');
  const personalHours = settings.find(s => s.key === 'pto.personal_hours_per_year');
  const vacationCap = settings.find(s => s.key === 'pto.vacation_accrual_cap');
  const sickCap = settings.find(s => s.key === 'pto.sick_accrual_cap');
  const personalCap = settings.find(s => s.key === 'pto.personal_accrual_cap');
  const startDelay = settings.find(s => s.key === 'pto.accrual_start_delay_days');
  const requireApproval = settings.find(s => s.key === 'pto.require_approval');
  const allowNegative = settings.find(s => s.key === 'pto.allow_negative_balance');

  // Helper for rendering a modern Switch
  const ModernSwitch = ({ checked, onChange, disabled, label }) => (
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
        checked ? 'bg-brand' : 'bg-gray-200'
      )}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </Switch>
  );

  // Render accrual method selector (collapsible)
  const renderAccrualMethodSelector = () => {
    if (!accrualMethod) return null;

    const value = getValue('pto.accrual_method') || 'lump_sum';
    const isDelayedEnabled = getValue('pto.delayed_start_enabled') === true;
    const delayDays = getValue('pto.delayed_start_days') || 90;

    return (
      <Disclosure>
        {({ open }) => (
          <div className={cn(
            'bg-white rounded-2xl border transition-all duration-200 overflow-hidden shadow-sm hover:shadow-md mb-6',
            open ? 'border-yellow-200 ring-1 ring-yellow-100' : 'border-gray-200'
          )}>
            <Disclosure.Button className="w-full flex items-center justify-between p-5 text-left outline-none">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-yellow-50 rounded-xl text-yellow-600">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Allocation Method</h3>
                  <p className="text-sm text-gray-500 font-medium">
                    {value === 'lump_sum' && 'Lump Sum - Full balance granted at once'}
                    {value === 'accrual' && 'Accrual - Earned over time'}
                    {value === 'responsible' && 'Responsible Time Off - Unlimited with approval'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {accrualMethod.valueSource !== 'default' && (
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md">
                    Customized
                  </span>
                )}
                <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform duration-200', open && 'rotate-180')} />
              </div>
            </Disclosure.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-5 pb-5 pt-0">
                <div className="h-px bg-gray-100 mb-5" />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Lump Sum */}
                  <button
                    onClick={() => updateDraft('pto.accrual_method', 'lump_sum')}
                    disabled={!accrualMethod.canEdit || saving}
                    className={cn(
                      'flex flex-col p-5 rounded-2xl border text-left transition-all duration-200 outline-none group relative overflow-hidden',
                      value === 'lump_sum'
                        ? 'bg-yellow-50 border-yellow-200 ring-1 ring-yellow-100 shadow-sm'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className="flex items-center justify-between mb-3 relative z-10">
                      <span className="font-bold text-gray-900">Lump Sum</span>
                      {value === 'lump_sum' && (
                        <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
                          <Check className="w-3 h-3 text-gray-900" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed relative z-10 font-medium">
                      Full annual balance granted at a specific time. Simple to manage.
                    </p>
                  </button>

                  {/* Accrual */}
                  <button
                    onClick={() => updateDraft('pto.accrual_method', 'accrual')}
                    disabled={!accrualMethod.canEdit || saving}
                    className={cn(
                      'flex flex-col p-5 rounded-2xl border text-left transition-all duration-200 outline-none group relative overflow-hidden',
                      value === 'accrual'
                        ? 'bg-yellow-50 border-yellow-200 ring-1 ring-yellow-100 shadow-sm'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className="flex items-center justify-between mb-3 relative z-10">
                      <span className="font-bold text-gray-900">Accrual</span>
                      {value === 'accrual' && (
                        <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
                          <Check className="w-3 h-3 text-gray-900" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed relative z-10 font-medium">
                      Employees earn PTO hours over time based on pay periods.
                    </p>
                  </button>

                  {/* Responsible Time Off */}
                  <button
                    onClick={() => updateDraft('pto.accrual_method', 'responsible')}
                    disabled={!accrualMethod.canEdit || saving}
                    className={cn(
                      'flex flex-col p-5 rounded-2xl border text-left transition-all duration-200 outline-none group relative overflow-hidden',
                      value === 'responsible'
                        ? 'bg-purple-50 border-purple-200 ring-1 ring-purple-100 shadow-sm'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <div className="flex items-center justify-between mb-3 relative z-10">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">Responsible Time Off</span>
                        <Infinity className="w-4 h-4 text-purple-500" />
                      </div>
                      {value === 'responsible' && (
                        <div className="w-5 h-5 bg-purple-400 rounded-full flex items-center justify-center shadow-sm">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed relative z-10 font-medium">
                      Unlimited PTO with manager approval required. Trust-based system.
                    </p>
                  </button>
                </div>

                {/* Responsible Time Off Info */}
                {value === 'responsible' && (
                  <div className="mt-6 p-5 bg-purple-50/50 rounded-2xl border border-purple-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-xl">
                        <Infinity className="w-4 h-4 text-purple-700" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-gray-900 mb-1">How Responsible Time Off Works</h4>
                        <ul className="text-xs text-gray-600 space-y-1.5 font-medium">
                          <li className="flex items-start gap-2">
                            <Check className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />
                            <span>Employees request time off through the portal or it's created manually</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />
                            <span>Requests require approval from admin, manager, or team lead</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />
                            <span>No balance tracking - employees take time as needed</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />
                            <span>All time off is still logged for reporting and payroll</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lump Sum Configuration */}
                {value === 'lump_sum' && (
                  <div className="mt-6 p-5 bg-yellow-50/50 rounded-2xl border border-yellow-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-yellow-100 rounded-xl">
                        <Calendar className="w-4 h-4 text-yellow-700" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">Grant Timing</h4>
                        <p className="text-xs text-gray-500 font-medium">When employees receive their full annual PTO balance</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => updateDraft('pto.lump_sum_grant_timing', 'year_start')}
                        disabled={!lumpSumGrantTiming?.canEdit || saving}
                        className={cn(
                          'flex flex-col p-4 rounded-xl border text-left transition-all duration-200 outline-none',
                          (getValue('pto.lump_sum_grant_timing') || 'year_start') === 'year_start'
                            ? 'bg-white border-yellow-300 ring-1 ring-yellow-200 shadow-sm'
                            : 'bg-white/50 border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold text-gray-900">Year Start</span>
                          {(getValue('pto.lump_sum_grant_timing') || 'year_start') === 'year_start' && (
                            <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-gray-900" />
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 leading-relaxed">
                          All employees receive their full balance on January 1st each year.
                        </p>
                      </button>

                      <button
                        onClick={() => updateDraft('pto.lump_sum_grant_timing', 'hire_anniversary')}
                        disabled={!lumpSumGrantTiming?.canEdit || saving}
                        className={cn(
                          'flex flex-col p-4 rounded-xl border text-left transition-all duration-200 outline-none',
                          getValue('pto.lump_sum_grant_timing') === 'hire_anniversary'
                            ? 'bg-white border-yellow-300 ring-1 ring-yellow-200 shadow-sm'
                            : 'bg-white/50 border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold text-gray-900">Hire Anniversary</span>
                          {getValue('pto.lump_sum_grant_timing') === 'hire_anniversary' && (
                            <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-gray-900" />
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 leading-relaxed">
                          Each employee receives their full balance on their work anniversary date.
                        </p>
                      </button>
                    </div>

                    {/* Delayed Start Toggle */}
                    <div className="mt-4 p-4 bg-white rounded-xl border border-yellow-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-50 rounded-lg">
                            <Hourglass className="w-4 h-4 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">Delayed Start for New Hires</p>
                            <p className="text-xs text-gray-500 font-medium">
                              Require a waiting period before employees become eligible
                            </p>
                          </div>
                        </div>
                        <ModernSwitch
                          checked={isDelayedEnabled}
                          onChange={(checked) => updateDraft('pto.delayed_start_enabled', checked)}
                          disabled={!delayedStartEnabled?.canEdit || saving}
                          label="Delayed Start"
                        />
                      </div>

                      {/* Delay Days Input - shown when enabled */}
                      {isDelayedEnabled && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs font-bold text-gray-700">Waiting Period</p>
                              <p className="text-[10px] text-gray-500">Days after hire before PTO eligibility</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={delayDays}
                                onChange={(e) => updateDraft('pto.delayed_start_days', parseInt(e.target.value) || 90)}
                                min={1}
                                max={365}
                                disabled={!delayedStartDays?.canEdit || saving}
                                className="w-20 px-3 py-2 text-sm font-bold text-center bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                              />
                              <span className="text-xs font-medium text-gray-500">days</span>
                            </div>
                          </div>
                          <p className="mt-3 text-[10px] text-orange-700 bg-orange-50 px-3 py-2 rounded-lg">
                            After the waiting period, employees will receive their PTO based on the selected grant timing above.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Accrual Configuration */}
                {value === 'accrual' && (
                  <div className="mt-6 p-5 bg-yellow-50/50 rounded-2xl border border-yellow-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-yellow-100 rounded-xl">
                        <Clock className="w-4 h-4 text-yellow-700" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">Accrual Configuration</h4>
                        <p className="text-xs text-gray-500 font-medium">How often employees earn their PTO hours</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Accrual Frequency */}
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">
                          Accrual Frequency
                        </label>
                        <select
                          value={getValue('pto.accrual_frequency') || 'biweekly'}
                          onChange={(e) => updateDraft('pto.accrual_frequency', e.target.value)}
                          disabled={!accrualFrequency?.canEdit || saving}
                          className="w-full px-4 py-2.5 text-sm font-bold bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none appearance-none"
                        >
                          <option value="weekly">Weekly (52 periods/year)</option>
                          <option value="biweekly">Bi-Weekly (26 periods/year)</option>
                          <option value="semimonthly">Semi-Monthly (24 periods/year)</option>
                          <option value="monthly">Monthly (12 periods/year)</option>
                        </select>
                      </div>

                      {/* Start Delay */}
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">
                          Accrual Start Delay
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={getValue('pto.accrual_start_delay_days') || 0}
                            onChange={(e) => updateDraft('pto.accrual_start_delay_days', parseInt(e.target.value) || 0)}
                            min={0}
                            max={365}
                            disabled={!startDelay?.canEdit || saving}
                            className="w-full pl-4 pr-16 py-2.5 text-sm font-bold bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">days after hire</span>
                        </div>
                      </div>
                    </div>

                    {/* Accrual Caps */}
                    <div className="mt-4">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-3 ml-1">
                        Maximum Balance Caps (0 = no cap)
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-white p-3 rounded-xl border border-gray-200">
                          <span className="text-[10px] font-bold text-blue-600 uppercase">Vacation Cap</span>
                          <div className="relative mt-1">
                            <input
                              type="number"
                              value={getValue('pto.vacation_accrual_cap') || 0}
                              onChange={(e) => updateDraft('pto.vacation_accrual_cap', parseInt(e.target.value) || 0)}
                              min={0}
                              disabled={!vacationCap?.canEdit || saving}
                              className="w-full pl-3 pr-10 py-1.5 text-sm font-bold border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400/20 focus:border-blue-400 outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">hrs</span>
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-xl border border-gray-200">
                          <span className="text-[10px] font-bold text-red-600 uppercase">Sick Cap</span>
                          <div className="relative mt-1">
                            <input
                              type="number"
                              value={getValue('pto.sick_accrual_cap') || 0}
                              onChange={(e) => updateDraft('pto.sick_accrual_cap', parseInt(e.target.value) || 0)}
                              min={0}
                              disabled={!sickCap?.canEdit || saving}
                              className="w-full pl-3 pr-10 py-1.5 text-sm font-bold border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-400/20 focus:border-red-400 outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">hrs</span>
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-xl border border-gray-200">
                          <span className="text-[10px] font-bold text-purple-600 uppercase">Personal Cap</span>
                          <div className="relative mt-1">
                            <input
                              type="number"
                              value={getValue('pto.personal_accrual_cap') || 0}
                              onChange={(e) => updateDraft('pto.personal_accrual_cap', parseInt(e.target.value) || 0)}
                              min={0}
                              disabled={!personalCap?.canEdit || saving}
                              className="w-full pl-3 pr-10 py-1.5 text-sm font-bold border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400/20 focus:border-purple-400 outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">hrs</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100 flex gap-3">
                      <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700 leading-relaxed font-medium">
                        Hours per period = Annual allocation ÷ periods per year. Example: 80 vacation hrs ÷ 26 bi-weekly periods = 3.08 hrs/period.
                      </p>
                    </div>
                  </div>
                )}
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    );
  };

  // Render default allocations section (collapsible)
  const renderDefaultAllocations = () => {
    const method = getValue('pto.accrual_method') || 'lump_sum';

    // Don't show allocations for Responsible Time Off
    if (method === 'responsible') {
      return (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-purple-50 rounded-xl text-purple-600">
              <Infinity className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">No Allocations Required</h3>
              <p className="text-sm text-gray-500 font-medium">
                Responsible Time Off doesn't track balances. Employees request time as needed.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <Disclosure>
        {({ open }) => (
          <div className={cn(
            'bg-white rounded-2xl border transition-all duration-200 overflow-hidden shadow-sm hover:shadow-md mb-6',
            open ? 'border-yellow-200 ring-1 ring-yellow-100' : 'border-gray-200'
          )}>
            <Disclosure.Button className="w-full flex items-center justify-between p-5 text-left outline-none">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-green-50 rounded-xl text-green-600">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Annual Allocations</h3>
                  <p className="text-sm text-gray-500 font-medium">Default hours for new employees</p>
                </div>
              </div>
              <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform duration-200', open && 'rotate-180')} />
            </Disclosure.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-5 pb-5 pt-0">
                <div className="h-px bg-gray-100 mb-5" />
                <p className="text-sm text-gray-500 mb-6 leading-relaxed font-medium">
                  These values are used when creating PTO balances for new employees.
                  Existing employee balances are not affected.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Vacation Hours */}
                  {vacationHours && (
                    <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 bg-blue-400 rounded-full" />
                        <span className="text-xs font-bold text-blue-900 uppercase tracking-wider">Vacation</span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          value={getValue('pto.vacation_hours_per_year') || 80}
                          onChange={(e) => updateDraft('pto.vacation_hours_per_year', parseInt(e.target.value) || 0)}
                          min={0}
                          max={520}
                          disabled={!vacationHours.canEdit || saving}
                          className="w-full pl-3 pr-12 py-2 text-sm font-bold bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400/20 focus:border-blue-400 outline-none transition-all"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-400">hrs/yr</span>
                      </div>
                    </div>
                  )}

                  {/* Sick Hours */}
                  {sickHours && (
                    <div className="bg-red-50/50 p-4 rounded-2xl border border-red-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 bg-red-400 rounded-full" />
                        <span className="text-xs font-bold text-red-900 uppercase tracking-wider">Sick Leave</span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          value={getValue('pto.sick_hours_per_year') || 40}
                          onChange={(e) => updateDraft('pto.sick_hours_per_year', parseInt(e.target.value) || 0)}
                          min={0}
                          max={520}
                          disabled={!sickHours.canEdit || saving}
                          className="w-full pl-3 pr-12 py-2 text-sm font-bold bg-white border border-red-200 rounded-xl focus:ring-2 focus:ring-red-400/20 focus:border-red-400 outline-none transition-all"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-red-400">hrs/yr</span>
                      </div>
                    </div>
                  )}

                  {/* Personal Hours */}
                  {personalHours && (
                    <div className="bg-purple-50/50 p-4 rounded-2xl border border-purple-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 bg-purple-400 rounded-full" />
                        <span className="text-xs font-bold text-purple-900 uppercase tracking-wider">Personal</span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          value={getValue('pto.personal_hours_per_year') || 16}
                          onChange={(e) => updateDraft('pto.personal_hours_per_year', parseInt(e.target.value) || 0)}
                          min={0}
                          max={520}
                          disabled={!personalHours.canEdit || saving}
                          className="w-full pl-3 pr-12 py-2 text-sm font-bold bg-white border border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400/20 focus:border-purple-400 outline-none transition-all"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-purple-400">hrs/yr</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Timer className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Hours per Work Day</p>
                      <p className="text-[10px] text-gray-500 font-medium">Used to calculate days from hours</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={getValue('pto.hours_per_day') || 8}
                      onChange={(e) => updateDraft('pto.hours_per_day', parseInt(e.target.value) || 8)}
                      min={1}
                      max={24}
                      disabled={saving}
                      className="w-16 px-3 py-1.5 text-sm font-bold bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                    />
                    <span className="text-xs font-bold text-gray-400 uppercase">hours</span>
                  </div>
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    );
  };

  // Render rules & approvals section (collapsible)
  const renderRulesSection = () => {
    const method = getValue('pto.accrual_method') || 'lump_sum';

    return (
      <Disclosure>
        {({ open }) => (
          <div className={cn(
            'bg-white rounded-2xl border transition-all duration-200 overflow-hidden shadow-sm hover:shadow-md',
            open ? 'border-yellow-200 ring-1 ring-yellow-100' : 'border-gray-200'
          )}>
            <Disclosure.Button className="w-full flex items-center justify-between p-5 text-left outline-none">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-blue-50 rounded-xl text-blue-600">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Rules & Approvals</h3>
                  <p className="text-sm text-gray-500 font-medium">Approval requirements and balance policies</p>
                </div>
              </div>
              <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform duration-200', open && 'rotate-180')} />
            </Disclosure.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-5 pb-5 pt-0">
                <div className="h-px bg-gray-100 mb-5" />
                <div className="space-y-4">
                  {/* Require Approval - always shown but forced on for Responsible */}
                  {requireApproval && (
                    <div className="group bg-gray-50 rounded-2xl border border-gray-200 p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-white rounded-xl text-gray-600 shadow-sm">
                          <UserCheck className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">Require Approval</h3>
                          <p className="text-sm text-gray-500 font-medium max-w-md">
                            {method === 'responsible'
                              ? 'Always required for Responsible Time Off'
                              : 'Require manager approval before PTO is deducted from balance'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {method === 'responsible' && (
                          <span className="text-[10px] uppercase tracking-wider font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded-md">
                            Required
                          </span>
                        )}
                        <ModernSwitch
                          checked={method === 'responsible' ? true : (getValue('pto.require_approval') === true)}
                          onChange={(checked) => updateDraft('pto.require_approval', checked)}
                          disabled={method === 'responsible' || !requireApproval.canEdit || saving}
                          label="Require Approval"
                        />
                      </div>
                    </div>
                  )}

                  {/* Allow Negative Balance - hidden for Responsible */}
                  {allowNegative && method !== 'responsible' && (
                    <div className="group bg-gray-50 rounded-2xl border border-gray-200 p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-white rounded-xl text-gray-600 shadow-sm">
                          <DollarSign className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">Allow Negative Balance</h3>
                          <p className="text-sm text-gray-500 font-medium max-w-md">
                            Allow employees to request more PTO than their current balance
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {allowNegative.valueSource !== 'default' && (
                          <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md">
                            Customized
                          </span>
                        )}
                        <ModernSwitch
                          checked={getValue('pto.allow_negative_balance') === true}
                          onChange={(checked) => updateDraft('pto.allow_negative_balance', checked)}
                          disabled={!allowNegative.canEdit || saving}
                          label="Allow Negative Balance"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Info Box */}
      <div className="bg-blue-600 rounded-2xl p-6 shadow-xl shadow-blue-100 text-white flex flex-col md:flex-row items-center gap-6">
        <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl shrink-0">
          <ShieldCheck className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h4 className="text-lg font-bold mb-1">PTO Management</h4>
          <p className="text-sm text-blue-100 leading-relaxed font-medium">
            Define how your team earns and uses paid time off. Set standard allocations for new hires and configure approval workflows.
          </p>
        </div>
        <div className="h-12 w-px bg-white/20 hidden md:block" />
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Annual Quotas</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Approvals</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Accrual Logic</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Responsible PTO</span>
          </div>
        </div>
      </div>

      {/* Accrual Method */}
      <section>
        <div className="flex items-center gap-2 mb-4 ml-1">
          <Calendar className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Earning Policy</h3>
        </div>
        {renderAccrualMethodSelector()}
      </section>

      {/* Default Allocations */}
      <section>
        <div className="flex items-center gap-2 mb-4 ml-1">
          <DollarSign className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Standard Allowances</h3>
        </div>
        {renderDefaultAllocations()}
      </section>

      {/* Rules & Approvals */}
      <section>
        <div className="flex items-center gap-2 mb-4 ml-1">
          <ShieldCheck className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Rules & Approvals</h3>
        </div>
        {renderRulesSection()}
      </section>
    </div>
  );
};

export default PTOSettingsPanel;
